import type { ApolloQueryResult, FetchMoreQueryOptions } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { t } from "i18next";

import { GET_ME_VULNERABILITY_DRAFTS } from "../queries";
import type { IGetMeVulnerabilityDrafts } from "../types";
import type { GetMeVulnerabilityDraftsQueryVariables } from "gql/graphql";
import { useAudit } from "hooks/use-audit";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";

interface IUseVulnerabilityDraftsQuery {
  data: IGetMeVulnerabilityDrafts | undefined;
  refetch: () => Promise<ApolloQueryResult<IGetMeVulnerabilityDrafts>>;
  fetchMore: (
    options: FetchMoreQueryOptions<unknown, IGetMeVulnerabilityDrafts>,
  ) => Promise<ApolloQueryResult<IGetMeVulnerabilityDrafts>>;
  loading: boolean;
}

const useVulnerabilityDraftsQuery = ({
  filters: {
    findingTitle,
    fromReportDate,
    groupName,
    hacker,
    maxSeverityTemporalScore,
    minSeverityTemporalScore,
    organizationName,
    sourceType,
    state,
    toReportDate,
  },
}: {
  filters: GetMeVulnerabilityDraftsQueryVariables;
}): IUseVulnerabilityDraftsQuery => {
  const { addAuditEvent } = useAudit();

  const { data, loading, refetch, fetchMore } =
    useQuery<IGetMeVulnerabilityDrafts>(GET_ME_VULNERABILITY_DRAFTS, {
      fetchPolicy: "network-only",
      onCompleted: (): void => {
        addAuditEvent("ToDo.VulnerabilityDrafts", "unknown");
      },
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((error): void => {
          msgError(t("groupAlerts.errorTextsad"));
          Logger.warning(
            "An error occurred fetching vulnerability drafts",
            error,
          );
        });
      },
      variables: {
        canRetrieveHacker: true,
        findingTitle: findingTitle === "" ? undefined : findingTitle,
        first: 100,
        fromReportDate: fromReportDate === "" ? undefined : fromReportDate,
        groupName: groupName === "" ? undefined : groupName,
        hacker: hacker === "" ? undefined : hacker,
        maxSeverityTemporalScore,
        minSeverityTemporalScore,
        organizationName:
          organizationName === "" ? undefined : organizationName,
        sourceType,
        state: state?.toUpperCase(),
        toReportDate,
      },
    });

  const returnRefetch = async (): Promise<
    ApolloQueryResult<IGetMeVulnerabilityDrafts>
  > => refetch();

  return { data, fetchMore, loading, refetch: returnRefetch };
};

export { useVulnerabilityDraftsQuery };
